import { ViewChild, inject } from '@angular/core';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { ReactiveFormsModule, FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';

import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { UserFacade } from '../../core/store/user/user.facade';
import { AuthService } from '../../core/services/auth.service';
import { User, UpdateAddressRequest, UpdateUserRequest } from '../../core/models/user.model';
import { UserService } from '../../core/services/user.service';
import { ViaCEPService, ViaCEPResponse } from '../../core/services/viacep.service';
import { ToastService } from '../../shared/services/toast.service';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { PasswordRequirementsComponent } from '../../shared/components/password-requirements/password-requirements.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { ErrorDisplayComponent } from '../../shared/components/error-display/error-display.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';

@Component({
  selector: 'umkt-account-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    CustomInputComponent,
    LoadingComponent,
    PasswordRequirementsComponent,
    ErrorDisplayComponent,
    I18nPipe
  ],
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.scss']
})
export class AccountDetailsComponent implements OnInit, OnDestroy {
  hasUserDataCharLimitError(): boolean {
    // Nome completo
    if (this.fullNameControl?.value && this.fullNameControl.value.length > 255) return true;
    // Email
    if (this.emailControl?.value && this.emailControl.value.length > 120) return true;
    return false;
  }

  hasUserDataFormChanged(): boolean {
    if (!this.originalUserDataFormValues) return false;
    
    const currentValues = this.userDataForm.value;
    const originalValues = this.originalUserDataFormValues;
    
    return (
      currentValues.fullName !== originalValues.fullName ||
      currentValues.email !== originalValues.email ||
      currentValues.phone !== originalValues.phone
    );
  }

  hasAddressFormChanged(): boolean {
    if (!this.originalAddressFormValues) return false;
    
    const currentValues = this.addressForm.value;
    const originalValues = this.originalAddressFormValues;
    
    return (
      currentValues.zipCode !== originalValues.zipCode ||
      currentValues.address !== originalValues.address ||
      currentValues.number !== originalValues.number ||
      currentValues.complement !== originalValues.complement ||
      currentValues.neighborhood !== originalValues.neighborhood ||
      currentValues.city !== originalValues.city ||
      currentValues.state !== originalValues.state
    );
  }

  hasAddressCharLimitError(): boolean {
    // Endereço
    if (this.addressControl?.value && this.addressControl.value.length > 255) return true;
    // Número
    if (this.numberControl?.value && this.numberControl.value.length > 20) return true;
    // Complemento
    if (this.complementControl?.value && this.complementControl.value.length > 100) return true;
    // Bairro
    if (this.neighborhoodControl?.value && this.neighborhoodControl.value.length > 100) return true;
    return false;
  }
  /**
   * Verifica se a cidade digitada está na lista de cidades carregadas (usado quando o CEP não é localizado)
   */
  cityWasSelectedFromList = false;

  isCitySelectedFromList(): boolean {
    // Só libera se a cidade foi selecionada da listagem
    if (!this.isEditingAddress) return true;
    return this.cityWasSelectedFromList;
  }

  // ======= CAMPOS DE ESTADO E CIDADE =======
  brazilStatesWithPlaceholder = [
    { value: '', label: 'Selecione o estado' },
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' }
  ];
  cities: string[] = [];
  filteredCities: string[] = [];
  showCitySuggestions = false;
  selectedSuggestionIndex = -1;
  selectedState = '';
  isLoadingCities = false;

  /**
   * Validador customizado para cidade: se não estiver na lista, retorna erro 'notInList'.
   */
  cityInListValidator = (control: AbstractControl) => {
    if (!control.value || !this.cities?.length) return null;
    // Permite se não estiver editando endereço
    if (!this.isEditingAddress) return null;
    const city = control.value.trim().toLowerCase();
    const found = this.cities.some(c => c.toLowerCase() === city);
    return found ? null : { notInList: true };
  };

  onStateChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const stateValue = target.value;
    this.selectedState = stateValue;
    const cityControl = this.addressForm.get('city');
    cityControl?.setValue('');
    this.showCitySuggestions = false;
    this.filteredCities = [];
    this.cityWasSelectedFromList = false;
    if (stateValue) {
      cityControl?.enable();
      this.loadCitiesFromIBGE(stateValue);
    } else {
      cityControl?.disable();
      this.cities = [];
    }
  }

  loadCitiesFromIBGE(stateCode: string): void {
    this.isLoadingCities = true;
    const apiUrl = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`;
    fetch(apiUrl)
      .then(res => res.json())
      .then((data: any[]) => {
        this.cities = data.map(city => city.nome).sort();
        this.isLoadingCities = false;
      })
      .catch(() => {
        this.cities = [];
        this.isLoadingCities = false;
      });
  }

  getCityPlaceholder(): string {
    if (!this.selectedState) {
      return 'Selecione um estado primeiro';
    }
    return 'Digite para buscar sua cidade';
  }

  onCityInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    if (!value || value.length < 1) {
      this.showCitySuggestions = false;
      this.filteredCities = [];
      return;
    }
    this.filteredCities = this.cities.filter(city =>
      city.toLowerCase().includes(value.toLowerCase())
    ).slice(0, 10);
    this.showCitySuggestions = this.filteredCities.length > 0;
    this.selectedSuggestionIndex = -1;
    this.cityWasSelectedFromList = false;
  }

  onCityFocus(): void {
    const currentValue = this.addressForm.get('city')?.value;
    if (currentValue && this.cities.length > 0) {
      this.onCityInput({ target: { value: currentValue } } as any);
    }
  }

  onCityKeydown(event: KeyboardEvent): void {
    if (!this.showCitySuggestions || this.filteredCities.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.min(
          this.selectedSuggestionIndex + 1,
          this.filteredCities.length - 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedSuggestionIndex >= 0) {
          this.selectCity(this.filteredCities[this.selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        this.showCitySuggestions = false;
        this.selectedSuggestionIndex = -1;
        break;
    }
  }

  selectCity(city: string): void {
    this.addressForm.get('city')?.setValue(city);
    this.cityWasSelectedFromList = true;
    this.showCitySuggestions = false;
    this.filteredCities = [];
    this.selectedSuggestionIndex = -1;
  }

  /** Não deixar escrever espaço no primeiro caractere */
  preventLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault(); // 🚫 não deixa digitar espaço no começo
    }
  }

  @ViewChild('newPasswordInput') newPasswordInput!: CustomInputComponent;
  @ViewChild('confirmPasswordInput') confirmPasswordInput!: CustomInputComponent;
  
  onPasswordToggle(isVisible: boolean): void {
    if (this.newPasswordInput) {
      this.newPasswordInput.setPasswordVisibility(isVisible);
    }
    if (this.confirmPasswordInput) {
      this.confirmPasswordInput.setPasswordVisibility(isVisible);
    }
  }

  // Impede espaço nos campos de senha
  preventSpace(event: KeyboardEvent) {
    if (event.key === ' ') {
      event.preventDefault();
    }
  }

  preventSpacePaste(event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData('text') || '';
    if (pasted.includes(' ')) {
      event.preventDefault();
    }
  }

  onlyNumbersPhone(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const value = target.value || '';
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'Home', 'End', 'ArrowLeft', 'ArrowRight', 'Clear'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (event.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }
    if (!/\d/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (value.length >= 15) {
      event.preventDefault();
    }
  }
  breadcrumbItems: BreadcrumbItem[] = [];
  cepError = false;
  user$: Observable<User | null>;
  isLoading = true;
  isLoadingCEP = false;
  isEditingUserData = false;
  isEditingAddress = false;
  isChangingPassword = false;
  showPassword = false; 
  showCurrentPassword = false; 
  currentUserUuid: string | null = null;
  currentAddressUuid: string | null = null; 
  originalUserData: User | null = null; 
  originalUserDataFormValues: any = null;
  originalAddressFormValues: any = null;
  loadError: string | null = null;
  retryCount = 0;
  
  private destroy$ = new Subject<void>();
  private langSubscription?: Subscription;

  passwordRequirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasSpecialChar: false,
    hasNumber: false
  };


  userDataForm: FormGroup;
  addressForm: FormGroup;
  changePasswordForm: FormGroup;
  private location = inject(Location);
  private router = inject(Router);
  private userFacade = inject(UserFacade);
  private userService = inject(UserService);
  private viaCEPService = inject(ViaCEPService);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private i18nService = inject(I18nService);

  constructor() {
    this.user$ = this.userFacade.getUserDetails();

    // Inicializar os forms com validações
    this.userDataForm = new FormGroup({
      fullName: new FormControl({ value: '', disabled: true }, [Validators.required, this.fullNameValidator, Validators.maxLength(255)]),
      email: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.email]),
      phone: new FormControl({ value: '', disabled: true }, [Validators.required, this.phoneValidator])
    });

    this.addressForm = new FormGroup({
      zipCode: new FormControl({ value: '', disabled: true }, [Validators.required, this.cepValidator]),
      address: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.maxLength(255)]),
      number: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.maxLength(20)]),
      complement: new FormControl({ value: '', disabled: true }),
      neighborhood: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]),
      city: new FormControl({ value: '', disabled: true }, [Validators.required, this.cityInListValidator]),
      state: new FormControl({ value: '', disabled: true }, [Validators.required])
    });

    this.changePasswordForm = new FormGroup({
      currentPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [Validators.required, this.passwordValidator.bind(this)]),
      confirmPassword: new FormControl('', [Validators.required])
    }, { validators: [this.passwordMatchValidator] });

    // Observar mudanças na senha nova para atualizar requisitos
    this.changePasswordForm.get('newPassword')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(password => {
      this.updatePasswordRequirements(password || '');
    });
  }

  ngOnInit(): void {
    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      this.buildBreadcrumb();
    });
    this.loadUserData();
    this.setupFormListeners();

    // Sempre resetar cityWasSelectedFromList ao digitar manualmente
    this.addressForm.get('city')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (!this.showCitySuggestions) {
        this.cityWasSelectedFromList = false;
      }
    });

  }

  private setupFormListeners(): void {
    let lastCepLength = '';
    this.addressForm.get('zipCode')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(cep => {
        const zipCodeCtrl = this.addressForm.get('zipCode');
        const currentLength = (cep || '').length;
        if (this.isEditingAddress && (lastCepLength && currentLength < lastCepLength.length)) {
          this.addressForm.patchValue({
            address: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: ''
          });
          this.addressForm.get('address')?.disable();
          this.addressForm.get('number')?.disable();
          this.addressForm.get('complement')?.disable();
          this.addressForm.get('neighborhood')?.disable();
          this.addressForm.get('city')?.disable();
          this.addressForm.get('state')?.disable();
          this.cepError = false;
        }
        lastCepLength = cep || '';
        if (cep && this.isEditingAddress && zipCodeCtrl?.dirty) {
          const formatted = this.viaCEPService.formatarCEP(cep);
          if (formatted !== cep) {
            this.addressForm.patchValue({ zipCode: formatted }, { emitEvent: false });
          }
          if (this.viaCEPService.validarCEP(cep)) {
            this.buscarEndereco(formatted);
          }
        }
      });
  }

  // Formatar telefone apenas para exibição
  get formattedPhone(): string {
    const value = this.userDataForm?.get('phone')?.value || '';
    return this.formatPhone(value);
  }


  private updatePasswordRequirements(password: string): void {
    this.passwordRequirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      hasNumber: /\d/.test(password)
    };
  }

  handleRetry() {
    this.retryCount++;
    this.loadUserData();
  }

  private loadUserData(): void {
    this.isLoading = true;
    this.loadError = null;

    this.userFacade.getUserUuid().pipe(
      take(1),
      switchMap(userUuid => {
        if (userUuid) {
          return this.userService.getUserByUuid(userUuid);
        }
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.populateFormsWithUserData(response.data);
        }
        this.retryCount = 0;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dados do usuário:', error);
        this.isLoading = false;
        this.loadError = 'Não foi possível carregar os dados da sua conta.';
      }
    });
  }

  private populateFormsWithUserData(user: User): void {
    this.currentUserUuid = user.uuid;
    this.originalUserData = { ...user };
    // Sempre aplicar a máscara ao carregar os dados
    const phoneDigits = (user.phone || '').replace(/\D/g, '');
    let phoneMasked = user.phone || '';
    if (phoneDigits.length === 11) {
      phoneMasked = phoneDigits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    } else if (phoneDigits.length === 10) {
      phoneMasked = phoneDigits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    
    const formValues = {
      fullName: `${user.firstName} ${user.lastName}`,
      email: user.email,
      phone: phoneMasked
    };
    
    this.userDataForm.patchValue(formValues);
    this.originalUserDataFormValues = { ...formValues };

    const mainAddress = user.addresses?.find(addr => addr.main) || user.addresses?.[0];
    if (mainAddress) {
      this.currentAddressUuid = mainAddress.uuid;

      this.addressForm.patchValue({
        zipCode: this.formatZipCode(mainAddress.zipCode),
        address: mainAddress.address,
        number: mainAddress.number,
        complement: mainAddress.complement || '',
        neighborhood: mainAddress.neighborhood,
        city: mainAddress.city,
        state: mainAddress.state
      });
    }
  }


  buildBreadcrumb(): void {
    this.breadcrumbItems = [
      { label: this.i18nService.instant('shared.labels.myAccount'), url: '/my-account' }
    ];

    if (this.isChangingPassword) {
      this.breadcrumbItems.push({
        label: this.i18nService.instant('pages.accountDetails.breadcrumb.details'),
        url: '/my-account/account-details'
      });
      this.breadcrumbItems.push({ 
        label: this.i18nService.instant('pages.accountDetails.breadcrumb.changePassword') 
      });
    } else {
      this.breadcrumbItems.push({ 
        label: this.i18nService.instant('pages.accountDetails.breadcrumb.details') 
      });
    }
  }

  backToAccountDetails(): void {
    this.cancelChangingPassword();
  }

  onBreadcrumbClick(item: BreadcrumbItem): void {
    if (item.url === '/my-account/account-details' && this.isChangingPassword) {
      this.backToAccountDetails();
    }
  }

  populateUserDataForm(user: User): void {
    this.userDataForm.patchValue({
      fullName: `${user.firstName} ${user.lastName}`,
      email: user.email,
      phone: this.formatPhone(user.phone)
    });
  }

  populateAddressForm(user: User): void {
    const address = this.getMainAddress(user);
    if (address) {
      this.addressForm.patchValue({
        zipCode: this.formatZipCode(address.zipCode),
        address: address.address,
        number: address.number,
        complement: address.complement || '',
        neighborhood: address.neighborhood,
        city: address.city,
        state: address.state
      });
    }
  }

  goBack(): void {
    this.location.back();
  }


  startChangingPassword(): void {
    this.isChangingPassword = true;
    this.buildBreadcrumb();


    this.changePasswordForm.reset();
    this.resetPasswordRequirements();
    this.showPassword = false;
    this.showCurrentPassword = false;
  }

  cancelChangingPassword(): void {
    this.isChangingPassword = false;
    this.buildBreadcrumb();

    this.changePasswordForm.reset();
    this.resetPasswordRequirements();
    this.showPassword = false;
    this.showCurrentPassword = false;
  }

  private resetPasswordRequirements(): void {
    this.passwordRequirements = {
      minLength: false,
      hasUpperCase: false,
      hasLowerCase: false,
      hasSpecialChar: false,
      hasNumber: false
    };
  }

  // ===== ALTERAÇÃO DE SENHA =====
  onPasswordSave(passwordData: any): void {
    if (this.currentUserUuid && this.originalUserData) {
      this.isLoading = true;
      this.userService.updateUser(this.currentUserUuid, {
        ...this.originalUserData,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      }).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (response) => {
          this.isLoading = false;
          // Limpa localStorage, desloga do store e redireciona imediatamente
          this.authService.logout();
          localStorage.clear();
          this.router.navigate(['/login']);
          this.toastService.showSuccess(
            this.i18nService.instant('pages.accountDetails.messages.passwordSuccess'),
            4000,
            'top-center'
          );
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Erro ao alterar senha:', error);
          let errorMessage = this.i18nService.instant('pages.accountDetails.messages.passwordError');
          if (error.status === 400) {
            errorMessage = this.i18nService.instant('pages.accountDetails.messages.passwordCurrentWrong');
          } else if (error.status === 422) {
            errorMessage = this.i18nService.instant('pages.accountDetails.messages.passwordRequirements');
          }
          this.toastService.showError(errorMessage, 5000, 'top-center');
        }
      });
    } else {
      this.toastService.showError(this.i18nService.instant('pages.accountDetails.messages.userDataUnavailable.'), 3000, 'top-center');
    }
  }

  onPasswordCancel(): void {
    this.isChangingPassword = false;
    this.buildBreadcrumb();
  }

  navigateToUpdateProfile(): void {
    this.router.navigate(['/update-profile']);
  }

  navigateToMyAccount(): void {
    this.router.navigate(['/my-account']);
  }

  startEditingUserData(): void {
    this.isEditingUserData = true;
    this.userDataForm.get('fullName')?.enable();
    this.userDataForm.get('email')?.enable();
    this.userDataForm.get('phone')?.enable();
    this.originalUserDataFormValues = { ...this.userDataForm.value };
  }

  cancelEditingUserData(): void {
    this.isEditingUserData = false;

    if (this.originalUserData) {
      this.populateFormsWithUserData(this.originalUserData);
    } else {
      this.userFacade.getUserUuid().pipe(
        take(1),
        switchMap(userUuid => {
          if (userUuid) {
            return this.userService.getUserByUuid(userUuid);
          }
          return of(null);
        })
      ).subscribe({
        next: (response) => {
          if (response && response.data) {
            this.populateFormsWithUserData(response.data);
          }
        },
        error: (error) => {
          console.error('Erro ao recarregar dados do usuário:', error);
        }
      });
    }

    this.userDataForm.get('fullName')?.disable();
    this.userDataForm.get('email')?.disable();
    this.userDataForm.get('phone')?.disable();
  }

  saveUserData(): void {
    if (this.userDataForm.valid && this.currentUserUuid && this.originalUserData) {
      const fullName = this.userDataForm.get('fullName')?.value?.trim() || '';
      const nameParts = fullName.split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const phoneDigits = (this.userDataForm.get('phone')?.value || '').replace(/\D/g, '').slice(0, 11);
      const updatePayload: UpdateUserRequest = {
        uuid: this.originalUserData.uuid,
        firstName: firstName,
        lastName: lastName,
        email: this.userDataForm.get('email')?.value,
        displayName: this.originalUserData.displayName,
        login: this.originalUserData.login,
        document: this.originalUserData.document,
        documentType: this.originalUserData.documentType,
        role: this.originalUserData.role,
        phone: phoneDigits,
        business: this.originalUserData.business,
        addresses: this.originalUserData.addresses.map(addr => ({
          uuid: addr.uuid,
          address: addr.address,
          neighborhood: addr.neighborhood,
          city: addr.city,
          state: addr.state,
          country: addr.country,
          zipCode: addr.zipCode,
          complement: addr.complement,
          main: addr.main,
          type: addr.type,
          number: addr.number
        }))
      };

      this.isLoading = true;

      this.userService.updateUser(this.currentUserUuid, updatePayload).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (response) => {
          this.isLoading = false;
          this.isEditingUserData = false;

          this.userDataForm.get('fullName')?.disable();
          this.userDataForm.get('email')?.disable();
          this.userDataForm.get('phone')?.disable();

          // Buscar dados mais atualizados após salvar
          this.loadUserData();

          this.toastService.showSuccess(
            this.i18nService.instant('pages.accountDetails.messages.userDataSuccess'),
            3000,
            'top-center'
          );
        },
        error: (error) => {
          console.error('Erro ao atualizar usuário:', error);
          this.isLoading = false;

          let errorMessage = error.error?.msg || this.i18nService.instant('pages.accountDetails.messages.userDataError');
          if (errorMessage.includes('already registered')) {
            errorMessage = this.i18nService.instant('pages.accountDetails.messages.emailTaken');
          }
          this.toastService.showError(errorMessage, 5000, 'top-center');
        }
      });
    } else {
      console.error('Formulário inválido ou dados necessários não encontrados');

      let errorMessage = 'pages.accountDetails.messages.internalError';

      if (!this.userDataForm.valid) {
        errorMessage = 'pages.accountDetails.messages.formInvalid';
      } else if (!this.currentUserUuid) {
        console.error('UUID do usuário não encontrado');
        errorMessage = 'pages.accountDetails.messages.userUuidNotFounds';
      } else if (!this.originalUserData) {
        console.error('Dados originais do usuário não encontrados');
        errorMessage = 'pages.accountDetails.messages.originalDataNotFound';
      }

      this.toastService.showError(this.i18nService.instant(errorMessage), 5000, 'top-center');
    }
  }

  startEditingAddress(): void {
    this.isEditingAddress = true;
    this.addressForm.get('zipCode')?.enable();
    this.addressForm.get('number')?.enable();
    this.addressForm.get('complement')?.enable();
    this.originalAddressFormValues = { ...this.addressForm.value };
  }

  cancelEditingAddress(): void {
    this.isEditingAddress = false;
    this.originalAddressFormValues = null;

    if (this.originalUserData) {
      this.populateFormsWithUserData(this.originalUserData);
    } else {
      this.userFacade.getUserUuid().pipe(
        take(1),
        switchMap(userUuid => {
          if (userUuid) {
            return this.userService.getUserByUuid(userUuid);
          }
          return of(null);
        })
      ).subscribe({
        next: (response) => {
          if (response && response.data) {
            this.populateFormsWithUserData(response.data);
          }
        },
        error: (error) => {
          console.error('Erro ao recarregar dados de endereço:', error);
        }
      });
    }
    this.addressForm.get('zipCode')?.disable();
    this.addressForm.get('address')?.disable();
    this.addressForm.get('number')?.disable();
    this.addressForm.get('complement')?.disable();
    this.addressForm.get('neighborhood')?.disable();
    this.addressForm.get('city')?.disable();
    this.addressForm.get('state')?.disable();
  }

  saveAddressData(): void {
    // Remove erro customizado se não houver erro de CEP
    const zipCodeCtrl = this.addressForm.get('zipCode');
    if (zipCodeCtrl?.errors && zipCodeCtrl.errors['notFound']) {
      const { notFound, ...rest } = zipCodeCtrl.errors;
      if (Object.keys(rest).length > 0) {
        zipCodeCtrl.setErrors(rest);
      } else {
        zipCodeCtrl.setErrors(null);
      }
    }
    if (this.addressForm.valid && this.currentUserUuid && this.currentAddressUuid) {
      // Preparar payload com dados do formulário usando interface tipada
      const addressPayload: UpdateAddressRequest = {
        address: this.addressForm.get('address')?.value,
        neighborhood: this.addressForm.get('neighborhood')?.value,
        city: this.addressForm.get('city')?.value,
        state: this.addressForm.get('state')?.value,
        country: "Brasil",
        zipCode: this.addressForm.get('zipCode')?.value?.replace(/\D/g, ''),
        complement: this.addressForm.get('complement')?.value || '',
        main: true,
        type: "SHIPPING",
        number: this.addressForm.get('number')?.value,
      };

      // Chamar API para atualizar endereço
      this.userService.updateUserAddress(
        this.currentUserUuid,
        this.currentAddressUuid,
        addressPayload
      ).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (response) => {
          this.isEditingAddress = false;
          this.originalAddressFormValues = null;

          // Desabilitar todos os campos após salvar
          this.addressForm.get('zipCode')?.disable();
          this.addressForm.get('address')?.disable();
          this.addressForm.get('number')?.disable();
          this.addressForm.get('complement')?.disable();
          this.addressForm.get('neighborhood')?.disable();
          this.addressForm.get('city')?.disable();
          this.addressForm.get('state')?.disable();

          // Buscar dados mais atualizados após salvar
          this.loadUserData();

          this.toastService.showSuccess(
            this.i18nService.instant('pages.accountDetails.messages.addressSuccess'),
            3000,
            'top-center'
          );
        },
        error: (error) => {
          console.error('Erro ao atualizar endereço:', error);

          const errorMessage = error.error?.msg || this.i18nService.instant('pages.accountDetails.messages.addressError');
          this.toastService.showError(errorMessage, 5000, 'top-center');
        }
      });
    } else {
      console.error('Formulário inválido ou UUIDs não encontrados');

      let errorMessage = 'Erro interno. Tente recarregar a página.';

      if (!this.addressForm.valid) {
        errorMessage = 'pages.accountDetails.messages.formInvalid';
      } else if (!this.currentUserUuid) {
        console.error('UUID do usuário não encontrado');
        errorMessage = 'pages.accountDetails.messages.userUuidNotFound';
      } else if (!this.currentAddressUuid) {
        console.error('UUID do endereço não encontrado');
        errorMessage = 'pages.accountDetails.messages.addressUuidNotFound';
      }

      this.toastService.showError(this.i18nService.instant(errorMessage), 5000, 'top-center');
    }
  }

  // Busca endereço pelo CEP
  buscarEndereco(cep: string): void {
    this.isLoadingCEP = true;
    this.cepError = false;
    this.viaCEPService.buscarCEP(cep).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: ViaCEPResponse) => {
        if (!response.erro) {
          this.addressForm.patchValue({
            address: response.logradouro,
            neighborhood: response.bairro,
            city: response.localidade,
            state: response.uf,
          });
          this.cepError = false;
          // Desabilita campos preenchidos automaticamente
          this.addressForm.get('address')?.disable();
          this.addressForm.get('neighborhood')?.disable();
          this.addressForm.get('city')?.disable();
          this.addressForm.get('state')?.disable();
          this.addressForm.get('number')?.enable();
          this.addressForm.get('complement')?.enable();
        } else {
          this.cepError = true;
          this.toastService.showError(
            this.i18nService.instant('pages.accountDetails.messages.cepNotFound'), 
            5000, 
            'top-center'
          );
          // Libera campos para preenchimento manual
          this.addressForm.get('address')?.enable();
          this.addressForm.get('neighborhood')?.enable();
          this.addressForm.get('state')?.enable();
          this.addressForm.get('number')?.enable();
          this.addressForm.get('complement')?.enable();
        }
        this.isLoadingCEP = false;
      },
      error: (error) => {
        this.viaCEPService.buscarCEPComFetch(cep).then(response => {
          if (!response.erro) {
            this.addressForm.patchValue({
              address: response.logradouro,
              neighborhood: response.bairro,
              city: response.localidade,
              state: response.uf,
            });
            this.cepError = false;
            this.addressForm.get('address')?.disable();
            this.addressForm.get('neighborhood')?.disable();
            this.addressForm.get('city')?.disable();
            this.addressForm.get('state')?.disable();
            this.addressForm.get('number')?.enable();
            this.addressForm.get('complement')?.enable();
          } else {
            this.cepError = true;
            this.toastService.showError(
            this.i18nService.instant('pages.accountDetails.messages.cepNotFound'), 
              5000, 
              'top-center'
            );
            this.addressForm.get('address')?.enable();
            this.addressForm.get('neighborhood')?.enable();
            this.addressForm.get('state')?.enable();
            this.addressForm.get('number')?.enable();
            this.addressForm.get('complement')?.enable();
          }
          this.isLoadingCEP = false;
        }).catch(fetchError => {
          const offlineResponse = this.viaCEPService.buscarCEPOffline(cep);
          if (offlineResponse) {
            this.addressForm.patchValue({
              address: offlineResponse.logradouro,
              neighborhood: offlineResponse.bairro,
              city: offlineResponse.localidade,
              state: offlineResponse.uf,
            });
            this.cepError = false;
            this.addressForm.get('address')?.disable();
            this.addressForm.get('neighborhood')?.disable();
            this.addressForm.get('city')?.disable();
            this.addressForm.get('state')?.disable();
            this.addressForm.get('number')?.enable();
            this.addressForm.get('complement')?.enable();
          } else {
            this.cepError = true;
            this.toastService.showError(
            this.i18nService.instant('pages.accountDetails.messages.cepNotFound'), 
              5000, 
              'top-center'
            );
            this.addressForm.get('address')?.enable();
            this.addressForm.get('neighborhood')?.enable();
            this.addressForm.get('state')?.enable();
            this.addressForm.get('number')?.enable();
            this.addressForm.get('complement')?.enable();
          }
          this.isLoadingCEP = false;
        });
      }
    });
  }

  getMainAddress(user: User | null): any {
    return user?.addresses?.find(address => address.main) || user?.addresses?.[0] || null;
  }

  formatDocument(document: string | undefined): string {
    if (!document) return '';

    // Formatar CNPJ: XX.XXX.XXX/XXXX-XX
    if (document.length === 14) {
      return document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }

    return document;
  }

  formatZipCode(zipCode: string | undefined): string {
    if (!zipCode) return '';

    // Formatar CEP: XXXXX-XXX
    if (zipCode.length === 8) {
      return zipCode.replace(/^(\d{5})(\d{3})$/, '$1-$2');
    }

    return zipCode;
  }

  formatPhone(phone: string | undefined): string {
    if (!phone) return '';

    // Formatar telefone: (XX) XXXXX-XXXX
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length === 11) {
      return cleanPhone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    } else if (cleanPhone.length === 10) {
      return cleanPhone.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }

    return phone;
  }

  formatCPF(cpf: string | undefined): string {
    if (!cpf) return '';

    // Formatar CPF: XXX.XXX.XXX-XX
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length === 11) {
      return cleanCPF.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }

    return cpf;
  }

  getFullAddress(address: any): string {
    if (!address) return '';

    const parts = [
      address.address,
      address.number,
      address.complement,
      address.neighborhood,
      address.city,
      address.state
    ].filter(part => part && part.trim());

    return parts.join(', ');
  }

  // Getters para os FormControls (para uso no template)
  get fullNameControl() {
    return this.userDataForm.get('fullName') as FormControl;
  }

  get emailControl() {
    return this.userDataForm.get('email') as FormControl;
  }

  get phoneControl() {
    return this.userDataForm.get('phone') as FormControl;
  }

  get zipCodeControl() {
    return this.addressForm.get('zipCode');
  }

  get addressControl() {
    return this.addressForm.get('address') as FormControl;
  }

  get numberControl() {
    return this.addressForm.get('number') as FormControl;
  }

  get complementControl() {
    return this.addressForm.get('complement') as FormControl;
  }

  get neighborhoodControl() {
    return this.addressForm.get('neighborhood') as FormControl;
  }

  get cityControl() {
    return this.addressForm.get('city') as FormControl;
  }

  get stateControl() {
    return this.addressForm.get('state') as FormControl;
  }

  get currentPasswordControl() {
    return this.changePasswordForm.get('currentPassword') as FormControl;
  }

  get newPasswordControl() {
    return this.changePasswordForm.get('newPassword') as FormControl;
  }

  get confirmNewPasswordControl() {
    return this.changePasswordForm.get('confirmPassword') as FormControl;
  }



  // Método para permitir apenas números no CEP (máximo 8 dígitos)
  onlyNumbersCEP(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value || '';
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'Clear'];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (currentValue.length >= 8) {
      event.preventDefault();
    }
  }

  onlyNumbers(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value || '';
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'Clear', 'Copy', 'Paste'];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const fieldName = target.getAttribute('formControlName');
    let maxLength = 0;

    if (fieldName === 'phone') {
      maxLength = 11;
    } else if (fieldName === 'zipCode') {
      maxLength = 8;
    }

    if (maxLength > 0 && currentValue.length >= maxLength) {
      event.preventDefault();
    }
  }

  phoneValidator(control: AbstractControl) {
    const phone = control.value?.replace(/\D/g, '');
    if (!phone) return null;

    if (phone.length < 10) {
      return { invalidPhone: true };
    }

    return null;
  }

  cepValidator(control: AbstractControl) {
    const cep = control.value?.replace(/\D/g, '');
    if (!cep) return null;

    if (cep.length !== 8) {
      return { invalidCEP: true };
    }

    return null;
  }

  fullNameValidator(control: AbstractControl) {
    const value = control.value?.trim();
    if (!value) return null;

    const words = value.split(/\s+/).filter((word: string) => word.length > 0);

    if (words.length < 2 || words.length === 0) {
      return { invalidFullName: true };
    }

    const hasValidWords = words.every((word: string) => word.length >= 2);
    if (!hasValidWords) {
      return { invalidFullName: true };
    }

    return null;
  }

  passwordValidator(control: AbstractControl) {
    const password = control.value;
    if (!password) return null;

    const requirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      hasNumber: /\d/.test(password)
    };

    const allRequirementsMet = Object.values(requirements).every(req => req === true);

    if (!allRequirementsMet) {
      return { invalidPassword: true };
    }

    return null;
  }

  passwordMatchValidator(formGroup: AbstractControl) {
    const newPassword = formGroup.get('newPassword')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      return { passwordMismatch: true };
    }

    return null;
  }

  updatePassword() {
    if (this.changePasswordForm.valid) {
      this.isLoading = true;
      this.userFacade.getUserUuid().pipe(
        take(1),
        switchMap(userUuid => {
          if (userUuid) {
            const formValues = this.changePasswordForm.value;
            return this.userService.updatePassword(
              userUuid,
              formValues.currentPassword,
              formValues.newPassword,
              formValues.confirmPassword
            );
          }
          throw new Error('UUID do usuário não encontrado');
        }),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          this.isLoading = false;
          this.authService.logout();
          localStorage.clear();
          this.router.navigate(['/login']);
          this.toastService.showSuccess(this.i18nService.instant('pages.accountDetails.messages.passwordSuccess'), 4000, 'top-center');
        },
        error: (error: any) => {
          // Tenta exibir a mensagem da API se existir
          let msg = 'pages.accountDetails.messages.passwordError';
          if (error?.error?.msg) {
            msg = error.error.msg;
            if (msg === 'Falha ao atualizar a senha.') {
              msg = 'pages.accountDetails.messages.passwordUpdateFail';
            }
          }
          this.toastService.showError(this.i18nService.instant(msg));
          console.error('Erro ao alterar senha:', error);
          this.isLoading = false;
        }
      });
    } else {
      this.changePasswordForm.markAllAsTouched();
      console.log('Form is invalid:', this.changePasswordForm.errors);
      console.log('Form controls errors:', {
        currentPassword: this.changePasswordForm.get('currentPassword')?.errors,
        newPassword: this.changePasswordForm.get('newPassword')?.errors,
        confirmPassword: this.changePasswordForm.get('confirmPassword')?.errors
      });
    }
  }
  applyPhoneMask(event: any): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const inputType = event.inputType;

    // Pega somente dígitos
    let value = input.value.replace(/\D/g, '');
    // Remove qualquer 12º dígito digitado
    if (value.length > 11) {
      value = value.slice(0, 11);
    }

    // Monta a máscara
    let formattedValue = '';
    if (value.length >= 2) {
      const ddd = value.substring(0, 2);
      const phoneNumber = value.substring(2);

      if (phoneNumber.length === 0) {
        formattedValue = `(${ddd}`;
      } else if (phoneNumber.length <= 4) {
        formattedValue = `(${ddd}) ${phoneNumber}`;
      } else if (phoneNumber.length <= 8) {
        const firstPart = phoneNumber.substring(0, 4);
        const secondPart = phoneNumber.substring(4);
        formattedValue = `(${ddd}) ${firstPart}-${secondPart}`;
      } else {
        const firstPart = phoneNumber.substring(0, 5);
        const secondPart = phoneNumber.substring(5);
        formattedValue = `(${ddd}) ${firstPart}-${secondPart}`;
      }
    } else if (value.length === 1) {
      formattedValue = `(${value}`;
    }

    // Aplica valor formatado ao input
    input.value = formattedValue;

    // Atualiza formControl sem disparar loop de evento
    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.userDataForm.get(controlName)?.setValue(formattedValue, { emitEvent: false });
    }

    // Reposiciona cursor se for backspace/delete
    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
      let newCursorPosition = cursorPosition;
      if (newCursorPosition > 0 && newCursorPosition <= formattedValue.length) {
        const charAtCursor = formattedValue.charAt(newCursorPosition - 1);
        if (['(', ')', ' ', '-'].includes(charAtCursor)) {
          newCursorPosition = Math.max(0, newCursorPosition - 1);
        }
      }
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  }

  toggleCurrentPasswordVisibility() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}